import api from "@/api/api";
import { isTeamWinner } from "@/common/isTeamWinner";

const TEAMS_PER_PAGE = 100
// страховка от бесконечного цикла, если бэк вернёт кривую пагинацию
const MAX_TEAMS_PAGES = 50

/**
 * Общая логика переноса команд в следующий этап прямо из турнирной сетки.
 *
 * Что должен предоставить компонент, который подключает миксин:
 *  - selectedStageId  — id текущего этапа (из moduleNewTournament)
 *  - transferTreeRoots — computed, массив массивов корневых узлов сеток
 *  - isFinalNode(node) — определение финального матча
 *  - openNodeModal(node) — открытие модалки матча
 *  - getGrids() — перезагрузка сетки
 *  - showError(message) — тост с ошибкой
 */
export const GridTransferMixin = {
    data() {
        return {
            stageTeams: [],
            stageTeamsRequest: null,
            isLoadingStageTeams: false,
            isTransferMode: false,
            isShowTransferModal: false,
            selectedTransferTeams: []
        }
    },
    computed: {
        // Переопределяется в компоненте: [[...корни верхней сетки], [...корни нижней]]
        transferTreeRoots() {
            return []
        }
    },
    methods: {
        /**
         * Команды этапа нужны ради stage_team_id — именно его ждёт ручка переноса,
         * в данных сетки его нет.
         *
         * Ходит постранично, поэтому: результат кэшируется, параллельные вызовы
         * переиспользуют один запрос, после переноса зовём с force.
         */
        async loadTeams({ force = false } = {}) {
            if (!force && this.stageTeams.length) return
            // запрос уже в полёте — ждём его, а не шлём второй такой же
            if (this.stageTeamsRequest) return this.stageTeamsRequest

            this.isLoadingStageTeams = true
            this.stageTeamsRequest = this.fetchStageTeams()

            try {
                this.stageTeams = await this.stageTeamsRequest
            } catch (error) {
                console.error('Error loading teams:', error)
                this.showError('Не удалось загрузить команды')
            } finally {
                this.stageTeamsRequest = null
                this.isLoadingStageTeams = false
            }
        },

        async fetchStageTeams() {
            const allTeams = []
            let page = 1

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const response = await api.teams.fromStage(this.selectedStageId, {
                    page,
                    // бэк отдаёт размер страницы в pagination.by, поэтому просим им же,
                    // per_page оставлен для совместимости
                    by: TEAMS_PER_PAGE,
                    per_page: TEAMS_PER_PAGE,
                    status: 'accepted'
                })

                const teams = response?.data?.stage_teams || []
                const pagination = response?.data?.pagination || {}

                allTeams.push(...teams)

                // пустая или неполная страница — дальше идти незачем
                if (!teams.length || teams.length < (pagination.by || TEAMS_PER_PAGE)) break

                const totalPages = Math.ceil(pagination.total / (pagination.by || teams.length))
                if (!totalPages || page >= totalPages) break

                if (page >= MAX_TEAMS_PAGES) {
                    console.warn(`Прервали загрузку команд этапа на ${ MAX_TEAMS_PAGES } странице`)
                    break
                }

                page++
            }

            return allTeams.map(stageTeam => ({
                id: stageTeam.team.id,
                name: stageTeam.team.name,
                stage_team_id: stageTeam.id
            }))
        },

        stageTeamIdByTeamId(teamId) {
            return this.stageTeams.find(({ id }) => id === teamId)?.stage_team_id || null
        },

        isTeamTransferable(team) {
            return !!this.stageTeamIdByTeamId(team.id)
        },

        isTeamPicked(team) {
            return this.selectedTransferTeams.some(({ id }) => id === team.id)
        },

        teamTooltip(team) {
            if (!this.isTransferMode) return ''
            if (!this.isTeamTransferable(team)) return 'Команда не найдена в этапе — перенос недоступен'
            return this.isTeamPicked(team) ? 'Убрать из переноса' : 'Добавить к переносу'
        },

        /**
         * Для сеток, где клик по матчу висит на обёртке: в обычном режиме
         * даём событию всплыть и открыть модалку матча.
         */
        onTeamClick(team, event) {
            if (!this.isTransferMode) return
            event.stopPropagation()
            this.toggleTransferTeam(team)
        },

        // Для сеток, где клик висит на самой команде
        onGridTeamClick(team, node) {
            if (this.isTransferMode) {
                this.toggleTransferTeam(team)
                return
            }
            this.openNodeModal(node)
        },

        toggleTransferTeam(team) {
            const stageTeamId = this.stageTeamIdByTeamId(team.id)

            if (!stageTeamId) {
                this.$bvToast.toast(`Не нашли команду "${ team.name }" в списке команд этапа`, {
                    title: 'Перенос недоступен',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 3000
                })
                return
            }

            const index = this.selectedTransferTeams.findIndex(({ id }) => id === team.id)

            if (index > -1) this.selectedTransferTeams.splice(index, 1)
            else this.selectedTransferTeams.push({ id: team.id, name: team.name, stage_team_id: stageTeamId })
        },

        clearTransferSelection() {
            this.selectedTransferTeams = []
        },

        collectTeamsFromTrees(predicate) {
            const collected = []

            const walk = (nodes) => {
                (nodes || []).forEach((node) => {
                    (node.teams || []).forEach((team) => {
                        if (predicate(team, node)) collected.push(team)
                    })
                    walk(node.children)
                })
            }

            this.transferTreeRoots.forEach((roots) => walk(roots))

            return collected
        },

        quickPick(mode) {
            const predicates = {
                winners: (team, node) => isTeamWinner(team, node.teams),
                // проигравшие берутся только из сыгранных матчей,
                // иначе попадут все участники несыгранных пар
                losers: (team, node) => node.teams.length > 1
                    && node.teams.some((matchTeam) => isTeamWinner(matchTeam, node.teams))
                    && !isTeamWinner(team, node.teams),
                finalists: (_, node) => this.isFinalNode(node)
            }

            const found = this.collectTeamsFromTrees(predicates[mode])
            const uniqueTeams = []
            let skippedCount = 0

            found.forEach((team) => {
                if (uniqueTeams.some(({ id }) => id === team.id)) return

                const stageTeamId = this.stageTeamIdByTeamId(team.id)
                if (!stageTeamId) {
                    skippedCount++
                    return
                }

                uniqueTeams.push({ id: team.id, name: team.name, stage_team_id: stageTeamId })
            })

            this.selectedTransferTeams = uniqueTeams

            if (!uniqueTeams.length) {
                this.$bvToast.toast('Под этот фильтр не подошла ни одна команда', {
                    title: 'Пусто',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 2000
                })
                return
            }

            this.$bvToast.toast(
                `Выбрано команд: ${ uniqueTeams.length }` + (skippedCount ? `, пропущено: ${ skippedCount }` : ''),
                {
                    title: 'Быстрый выбор',
                    variant: 'info',
                    solid: true,
                    autoHideDelay: 2000
                }
            )
        },

        async handleTransferSubmitted() {
            this.isTransferMode = false
            // состав этапа изменился — перечитываем
            await this.loadTeams({ force: true })
            await this.getGrids()
        },

        handleTransferModeKeydown(event) {
            if (event.key !== 'Escape' || !this.isTransferMode || this.isShowTransferModal) return
            this.isTransferMode = false
        }
    },
    watch: {
        isTransferMode(value) {
            if (!value) {
                this.clearTransferSelection()
                return
            }
            // команды этапа нужны только для переноса — тянем их при входе в режим,
            // если сетка не загрузила их раньше для своих нужд
            this.loadTeams()
        }
    },
    mounted() {
        document.addEventListener('keydown', this.handleTransferModeKeydown)
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleTransferModeKeydown)
    }
}
